import { useState, useEffect } from 'react';
import { X, Calendar, Clock, DollarSign, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingModal({ service, onClose }) {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    loadAvailability();
  }, []);

  const loadAvailability = async () => {
    try {
      const today = new Date();
      const endDate = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);
      
      const response = await axios.get(`${API}/availability/${service.id}`, {
        params: {
          start_date: today.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      setAvailability(response.data);
    } catch (error) {
      toast.error('Failed to load availability');
    }
  };

  const getAvailableDates = () => {
    if (!availability) return [];
    
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 90; i++) {
      const date = new Date(today.getTime() + i * 24 * 60 * 60 * 1000);
      const dayOfWeek = date.getDay();
      const dateStr = date.toISOString().split('T')[0];
      
      if ((dayOfWeek === 1 || dayOfWeek === 5) && !availability.blocked_dates.includes(dateStr)) {
        dates.push({
          date: dateStr,
          display: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        });
      }
    }
    
    return dates;
  };

  const getAvailableTimes = (date) => {
    if (!availability || !date) return [];
    
    const times = ['13:00', '13:45', '14:30'];
    const bookedTimes = availability.booked_slots[date] || [];
    
    return times.filter(time => !bookedTimes.includes(time)).map(time => ({
      value: time,
      display: new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    }));
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select date and time');
      return;
    }
    
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    setStep(3);
    
    try {
      const bookingDateTime = `${selectedDate}T${selectedTime}:00Z`;
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/bookings`, {
        service_id: service.id,
        user_name: formData.name,
        user_email: formData.email,
        user_phone: formData.phone,
        booking_date: bookingDateTime,
        notes: formData.notes,
        origin_url: originUrl
      });
      
      if (response.data.payment_url) {
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-gradient-to-br from-[#3D2817] via-[#2C1810] to-[#3D2817] max-w-2xl w-full mx-4 p-8 relative max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl border-4 border-[#E8B67A]">
        <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-[#E8B67A]">
          <X className="w-6 h-6" />
        </button>

        <div className="mb-6">
          <h2 className="text-3xl retro-title text-white mb-2">{service.name}</h2>
          <div className="flex flex-wrap items-center gap-4 text-white">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>{service.duration_minutes} minutes</span>
            </div>
            <div className="flex items-center space-x-2">
              <DollarSign className="w-4 h-4" />
              <span>${service.price}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Video className="w-4 h-4" />
              <span>Via Zoom</span>
            </div>
          </div>
          <p className="text-white mt-3">{service.description}</p>
        </div>

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-white font-bold mb-3">Select Date</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                {getAvailableDates().map(({ date, display }) => (
                  <button
                    key={date}
                    onClick={() => {
                      setSelectedDate(date);
                      setSelectedTime('');
                    }}
                    className={`py-3 px-2 border-2 border-[#E8B67A] rounded font-bold ${
                      selectedDate === date ? 'bg-[#E8B67A] text-[#2C1810]' : 'bg-[#2C1810]/50 text-white hover:bg-[#2C1810]/70'
                    }`}
                  >
                    {display}
                  </button>
                ))}
              </div>
            </div>

            {selectedDate && (
              <div>
                <label className="block text-white font-bold mb-3">Select Time</label>
                <div className="grid grid-cols-3 gap-2">
                  {getAvailableTimes(selectedDate).map(({ value, display }) => (
                    <button
                      key={value}
                      onClick={() => setSelectedTime(value)}
                      className={`py-3 px-4 border-2 border-[#E8B67A] rounded font-bold ${
                        selectedTime === value ? 'bg-[#E8B67A] text-[#2C1810]' : 'bg-[#2C1810]/50 text-white hover:bg-[#2C1810]/70'
                      }`}
                    >
                      {display}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => setStep(2)} disabled={!selectedDate || !selectedTime} className="retro-button w-full disabled:opacity-50">
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-[#E8B67A]/20 p-4 rounded mb-4 border-2 border-[#E8B67A]">
              <p className="text-white font-semibold">
                <Calendar className="w-4 h-4 inline mr-2" />
                {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {
                  new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                }
              </p>
            </div>

            <input type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400" />
            <input type="email" placeholder="Email Address *" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400" />
            <input type="tel" placeholder="Phone Number (optional)" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400" />
            <textarea placeholder="Any specific topics you'd like to discuss? (optional)" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded bg-[#2C1810]/50 text-white placeholder-gray-400" />

            <div className="flex space-x-3">
              <button onClick={() => setStep(1)} className="retro-button flex-1 bg-[#8B5A3C]">Back</button>
              <button onClick={handleSubmit} disabled={loading} className="retro-button flex-1">Proceed to Payment</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 border-4 border-[#E8B67A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white text-lg">Processing your booking...</p>
          </div>
        )}
      </div>
    </div>
  );
}
